import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../contexts/UserContext';
import api from '../../utils/api';
import EmergencyResources from '../../components/disclaimers/EmergencyResources';

const ContactContainer = styled.div`
  padding: 3rem 0;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  
  @media (min-width: 768px) {
    grid-template-columns: 3fr 2fr;
  }
`;

const ContactForm = styled.form`
  background-color: white;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  padding: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  transition: border-color 0.3s ease;
  
  &:focus {
    border-color: var(--primary);
    outline: none;
    box-shadow: 0 0 0 3px rgba(233, 64, 87, 0.1);
  }
  
  ${props => props.error && `
    border-color: #dc3545;
    
    &:focus {
      box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
    }
  `}
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  transition: border-color 0.3s ease;
  min-height: 150px;
  resize: vertical;
  
  &:focus {
    border-color: var(--primary);
    outline: none;
    box-shadow: 0 0 0 3px rgba(233, 64, 87, 0.1);
  }
  
  ${props => props.error && `
    border-color: #dc3545;
    
    &:focus {
      box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
    }
  `}
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  transition: border-color 0.3s ease;
  
  &:focus {
    border-color: var(--primary);
    outline: none;
    box-shadow: 0 0 0 3px rgba(233, 64, 87, 0.1);
  }
  
  ${props => props.error && `
    border-color: #dc3545;
    
    &:focus {
      box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
    }
  `}
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.85rem;
  margin-top: 0.5rem;
`;

const SubmitButton = styled.button`
  background-color: var(--primary);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s ease;
  
  &:hover {
    background-color: var(--primary-dark);
  }
  
  &:disabled {
    background-color: #e0e0e0;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled.div`
  background-color: #d4edda;
  color: #155724;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const GeneralErrorMessage = styled.div`
  background-color: #f8d7da;
  color: #721c24;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const ContactPage = () => {
  const { isAuthenticated } = useAuth();
  const { firstName, lastName, email, loading } = useUser();
  const [randomImage, setRandomImage] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [captcha, setCaptcha] = useState({ question: '', answer: 0 });
  const [userCaptchaAnswer, setUserCaptchaAnswer] = useState('');
  
  // Set random image on component mount
  useEffect(() => {
    const images = [
      'the-national-library-of-norway--JCUscKidno-unsplash.jpeg',
      'polox-hernandez-v1yPWKaAVkY-unsplash.jpg'
    ];
    const randomIndex = Math.floor(Math.random() * images.length);
    setRandomImage(images[randomIndex]);
  }, []);
  
  // Generate a simple math captcha
  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10);
    const num2 = Math.floor(Math.random() * 10);
    setCaptcha({
      question: `What is ${num1} + ${num2}?`,
      answer: num1 + num2
    });
    setUserCaptchaAnswer('');
  };
  
  // Generate captcha on component mount
  useEffect(() => {
    generateCaptcha();
  }, []);
  
  // Pre-fill form with user data if authenticated
  useEffect(() => {
    if (isAuthenticated && !loading && firstName && lastName && email) {
      setFormData(prev => ({
        ...prev,
        name: `${firstName} ${lastName}`,
        email: email
      }));
    }
  }, [isAuthenticated, loading, firstName, lastName, email]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  const handleCaptchaChange = (e) => {
    setUserCaptchaAnswer(e.target.value);
    // Clear error when user starts typing
    if (errors.captcha) {
      setErrors(prev => ({ ...prev, captcha: '' }));
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }
    
    // Validate captcha
    if (!userCaptchaAnswer) {
      newErrors.captcha = 'Please answer the security question';
    } else if (parseInt(userCaptchaAnswer, 10) !== captcha.answer) {
      newErrors.captcha = 'Incorrect answer. Please try again.';
      generateCaptcha(); // Generate a new captcha question
    }
    
    return newErrors;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }
    
    setIsSubmitting(true);
    
    // Call the contact form API
    api.post('/contact', formData)
      .then(response => {
        console.log('Form submitted successfully:', response.data);
        setIsSubmitting(false);
        setSubmitSuccess(true);
        setFormData({
          name: isAuthenticated ? `${firstName} ${lastName}` : '',
          email: isAuthenticated ? email : '',
          subject: '',
          message: ''
        });
        
        // Generate new captcha
        generateCaptcha();
        
        // Reset success message after 5 seconds
        setTimeout(() => {
          setSubmitSuccess(false);
        }, 5000);
      })
      .catch(error => {
        console.error('Error submitting form:', error);
        setIsSubmitting(false);
        
        // Handle validation errors from the API
        if (error.response && error.response.data && error.response.data.errors) {
          const apiErrors = {};
          error.response.data.errors.forEach(err => {
            if (err.includes('Name')) apiErrors.name = err;
            else if (err.includes('Email')) apiErrors.email = err;
            else if (err.includes('Subject')) apiErrors.subject = err;
            else if (err.includes('Message')) apiErrors.message = err;
            else apiErrors.general = err;
          });
          setErrors(apiErrors);
        } else {
          // Generic error
          setErrors({
            general: 'An error occurred while submitting the form. Please try again later.'
          });
        }
      });
  };
  
  return (
    <>
      {/* Page Header */}
      <section className="py-5 bg-primary text-white text-center">
        <div className="container py-2">
          <h1>Contact Us</h1>
          <p className="mx-auto" style={{ maxWidth: '700px' }}>
            Have questions or need support? We're here to help you and your family get better sleep.
          </p>
        </div>
      </section>
      
      <div className="container">
        <ContactContainer>
          <ContactGrid>
            <ContactForm onSubmit={handleSubmit}>
              <h2 className="mb-4">Send Us a Message</h2>
              
              {submitSuccess && (
                <SuccessMessage>
                  Thank you for your message! We'll get back to you as soon as possible.
                </SuccessMessage>
              )}
              
              {errors.general && (
                <GeneralErrorMessage>
                  {errors.general}
                </GeneralErrorMessage>
              )}
              
              <FormGroup>
                <FormLabel htmlFor="name">Your Name</FormLabel>
                <FormInput
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  error={errors.name}
                />
                {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="email">Email Address</FormLabel>
                <FormInput
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={errors.email}
                />
                {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="subject">Subject</FormLabel>
                <FormSelect
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  error={errors.subject}
                >
                  <option value="">Select a subject</option>
                  <option value="General Inquiry">General Inquiry</option>
                  <option value="Technical Support">Technical Support</option>
                  <option value="Sleep Plan Question">Sleep Plan Question</option>
                  <option value="Billing Question">Billing Question</option>
                  <option value="Partnership Opportunity">Partnership Opportunity</option>
                  <option value="Other">Other</option>
                </FormSelect>
                {errors.subject && <ErrorMessage>{errors.subject}</ErrorMessage>}
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="message">Your Message</FormLabel>
                <FormTextarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  error={errors.message}
                />
                {errors.message && <ErrorMessage>{errors.message}</ErrorMessage>}
              </FormGroup>
              
              {/* CAPTCHA */}
              <FormGroup>
                <FormLabel htmlFor="captcha">{captcha.question} (we need to make sure you a human)</FormLabel>
                <FormInput
                  type="number"
                  id="captcha"
                  value={userCaptchaAnswer}
                  onChange={handleCaptchaChange}
                  error={errors.captcha}
                  placeholder="Enter your answer"
                />
                {errors.captcha && <ErrorMessage>{errors.captcha}</ErrorMessage>}
              </FormGroup>
              
              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </SubmitButton>
            </ContactForm>
            
            <div>
              
              <div className="contact-image-container" style={{ 
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
              }}>
                <img 
                  src={`/images/${randomImage}`} 
                  alt="Contact visual" 
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </div>
              <br />
              <EmergencyResources />
              
            </div>
          </ContactGrid>
        </ContactContainer>
      </div>
    </>
  );
};

export default ContactPage;
